import { keepPreviousData, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReloadCollection } from "@repo/shared";

import { ApiError } from "@/lib/api-client";
import {
  getLiveReloads,
  getReloadAlerts,
  getReloadAppRuns,
  getReloadApps,
  getReloadBottlenecks,
  getReloadCollection,
  getReloadErrors,
  getReloadLog,
  getReloadPerformance,
  setReloadCollection,
  type ReloadPeriodParams,
} from "@/lib/api/reloads";

// The collected views are keyed by `version` (the collection's lastCollectedAt):
// a finished collection changes it, which refetches them, while the previous
// numbers stay on screen (keepPreviousData) until the new ones arrive.
export const reloadKeys = {
  all: ["reloads"] as const,
  collection: (connectionId: string) => [...reloadKeys.all, connectionId, "collection"] as const,
  live: (connectionId: string) => [...reloadKeys.all, connectionId, "live"] as const,
  period: (connectionId: string, view: string, params: ReloadPeriodParams, version: string) =>
    [...reloadKeys.all, connectionId, view, params, version] as const,
  alerts: (connectionId: string, version: string) =>
    [...reloadKeys.all, connectionId, "alerts", version] as const,
};

const LIVE_POLL_MS = 15_000;

// Polls fast while a collection runs, then slowly so "collected N min ago" and
// a collection started by the scheduler show up without a reload.
export const reloadCollectionQueryOptions = (connectionId: string) =>
  queryOptions({
    queryKey: reloadKeys.collection(connectionId),
    queryFn: () => getReloadCollection(connectionId),
    enabled: !!connectionId,
    refetchInterval: (query) => (query.state.data?.running ? 5_000 : 60_000),
  });

// Straight from Qlik (the worker caches it for 15 s), so polling faster gains
// nothing.
export const liveReloadsQueryOptions = (connectionId: string) =>
  queryOptions({
    queryKey: reloadKeys.live(connectionId),
    queryFn: () => getLiveReloads(connectionId),
    enabled: !!connectionId,
    refetchInterval: LIVE_POLL_MS,
    retry: false,
  });

const periodOptions = <T>(
  view: string,
  fetch: (connectionId: string, params: ReloadPeriodParams) => Promise<T>,
) => {
  return (connectionId: string, params: ReloadPeriodParams, version: string) =>
    queryOptions({
      queryKey: reloadKeys.period(connectionId, view, params, version),
      queryFn: () => fetch(connectionId, params),
      enabled: !!connectionId,
      placeholderData: keepPreviousData,
      staleTime: Infinity,
    });
};

export const reloadErrorsQueryOptions = periodOptions("errors", getReloadErrors);
export const reloadBottlenecksQueryOptions = periodOptions("bottlenecks", getReloadBottlenecks);
export const reloadAppsQueryOptions = periodOptions("apps", getReloadApps);
export const reloadPerformanceQueryOptions = periodOptions("performance", getReloadPerformance);

export const reloadAppRunsQueryOptions = (
  connectionId: string,
  appId: string,
  params: ReloadPeriodParams,
  version: string,
) =>
  queryOptions({
    queryKey: [...reloadKeys.period(connectionId, "runs", params, version), appId] as const,
    queryFn: () => getReloadAppRuns(connectionId, appId, params),
    enabled: !!connectionId && !!appId,
    staleTime: Infinity,
  });

// Read live from Qlik, which may drop it any time: no infinite staleTime, and a
// refusal (gone, other space, too large) is an answer, not a retry.
export const reloadLogQueryOptions = (connectionId: string, appId: string, reloadId: string) =>
  queryOptions({
    queryKey: [...reloadKeys.all, connectionId, "log", appId, reloadId] as const,
    queryFn: () => getReloadLog(connectionId, appId, reloadId),
    retry: false,
  });

export const reloadAlertsQueryOptions = (connectionId: string, version: string) =>
  queryOptions({
    queryKey: reloadKeys.alerts(connectionId, version),
    queryFn: () => getReloadAlerts(connectionId),
    enabled: !!connectionId,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

export function useSetReloadCollection(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => setReloadCollection(connectionId, enabled),
    onSuccess: (collection: ReloadCollection) =>
      queryClient.setQueryData(reloadKeys.collection(connectionId), collection),
  });
}

// The connection's identity cannot list the tenant's reloads (Qlik 403): the
// API forwards Qlik's status in the error details.
export function isQlikForbidden(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.details?.upstreamStatus === 403 || error.details?.reason === "forbidden")
  );
}
